import { useEffect, useMemo, useRef, useState } from "react";
import { AccountingReview } from "./AccountingReview";
import { formatAccountCode, formatAccountCodeText, formatMoneyText, moneyNumber } from "../formatting";

import {
  api,
  apiFile,
  type PortalReportPreview,
  type RefundCase,
  type ReportsOverview,
  type SubmissionPackage
} from "../api";

const reportStatusLabels: Record<string, string> = {
  ready: "Hazır",
  completed: "Tamamlandı",
  conflict: "Uyuşmazlık var",
  matched: "Eşleşti",
  pending: "Bekliyor",
  blocked: "Eksikler var",
  missing: "Hazır değil",
  draft: "Hazırlanıyor",
  review: "Kontrolde",
  queued: "Kuyrukta",
  running: "Çalışıyor",
  processing: "İşleniyor",
  succeeded: "Başarılı",
  partial: "Kısmi",
  submitted: "Gönderildi",
  failed: "Başarısız",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  resolved: "Çözüldü",
  disabled: "Devre dışı"
};

function reportStatus(value: string) {
  return reportStatusLabels[value] || value;
}

function messageText(value: ReportsOverview["portal"]["blockers"][number]) {
  if (typeof value === "string") return formatMoneyText(value);
  return formatMoneyText(value.message || value.detail || value.label || "Eksik bilgi");
}

function messageListCode(value: ReportsOverview["portal"]["blockers"][number]) {
  if (typeof value === "string") return "";
  return String(value.metadata?.list_code || "");
}

function saveBlob(blob: Blob, filename: string | null, fallback: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || fallback;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function ReportsPage({
  taxpayer,
  cases,
  canPrepare,
  canApprove
}: {
  taxpayer: string;
  cases: RefundCase[];
  canPrepare: boolean;
  canApprove: boolean;
}) {
  const availableCases = useMemo(() => cases.filter((item) => !item.deleted_at), [cases]);
  const [caseId, setCaseId] = useState<number | null>(availableCases[0]?.id ?? null);
  const [overview, setOverview] = useState<ReportsOverview | null>(null);
  const [preview, setPreview] = useState<PortalReportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const previewRef = useRef<HTMLElement | null>(null);

  const selectedCase = availableCases.find((item) => item.id === caseId) || null;

  useEffect(() => {
    if (caseId !== null && availableCases.some((item) => item.id === caseId)) return;
    setCaseId(availableCases[0]?.id ?? null);
  }, [availableCases, caseId]);

  async function load(selectedId: number) {
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      setOverview(await api<ReportsOverview>(`/api/refund-cases/${selectedId}/reports`));
    } catch (reason) {
      setOverview(null);
      setError(reason instanceof Error ? reason.message : "Raporlar yüklenemedi.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (caseId !== null) void load(caseId);
    else setOverview(null);
  }, [caseId]);

  useEffect(() => {
    if (!preview) return;
    const frame = window.requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [preview]);

  async function showPreview(code: string) {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      setPreview(await api<PortalReportPreview>(`/api/refund-cases/${caseId}/reports/${code}/preview`));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Önizleme açılamadı.");
    } finally {
      setBusy(false);
    }
  }

  async function createPackage() {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      await api<SubmissionPackage>(`/api/refund-cases/${caseId}/submission-packages`, { method: "POST" });
      await load(caseId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Başvuru paketi hazırlanamadı.");
      setBusy(false);
    }
  }

  async function prepareInputVat() {
    if (caseId === null) return;
    const selectedId = caseId;
    setBusy(true);
    setError("");
    try {
      await api<{ row_count: number }>(`/api/refund-cases/${selectedId}/prepare-input?mode=reports`, { method: "POST" });
      await load(selectedId);
      await showPreview("input_vat");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "İndirilecek KDV listesi hazırlanamadı.");
    } finally {
      setBusy(false);
    }
  }

  async function approvePackage(packageId: number) {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      await api<SubmissionPackage>(`/api/submission-packages/${packageId}/approve`, {
        method: "POST",
        body: JSON.stringify({ note: "Raporlar ekranından kontrol edildi." })
      });
      await load(caseId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Paket onaylanamadı.");
      setBusy(false);
    }
  }

  async function downloadPackage(packageId: number) {
    setBusy(true);
    setError("");
    try {
      const file = await apiFile(`/api/submission-packages/${packageId}/artifact`);
      saveBlob(file.blob, file.filename, "kdv-iade-paketi.zip");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Paket indirilemedi.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadCapacityRecipes() {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      const file = await apiFile(`/api/refund-cases/${caseId}/capacity-recipes/export`);
      saveBlob(file.blob, file.filename, "kapasite-receteleri.xlsx");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Kapasite reçetesi indirilemedi.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadLoadedVatReview() {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      const file = await apiFile(`/api/refund-cases/${caseId}/export/loaded_vat?mode=review`);
      saveBlob(file.blob, file.filename, "yuklenilen-kdv-kontrol.xls");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Yüklenilen KDV listesi indirilemedi.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplates() {
    setError("");
    try {
      const file = await apiFile("/api/templates");
      saveBlob(file.blob, file.filename, "vrp-templateler.zip");
    } catch (reason) { setError(String(reason)); }
  }
  const templatesButton = <button className="secondary-button" onClick={() => void downloadTemplates()}>Templateleri İndir</button>;
  if (!availableCases.length) {
    return <><CustomsExports taxpayer={taxpayer} /><section className="panel">{templatesButton}{error && <p role="alert">{error}</p>}<div className="empty-state">Rapor hazırlanabilecek bir iade dosyası bulunmuyor.</div></section></>;
  }

  const latestPackage = overview?.latest_package;

  return <div className="reports-page">
    <CustomsExports taxpayer={taxpayer} />
    {templatesButton}
    <section className="panel reports-case-panel">
      <div><p className="eyebrow">RAPOR KAPSAMI</p><h2>İade dosyası seçin</h2></div>
      <label>İade dosyası<select disabled={busy} value={caseId ?? ""} onChange={(event) => setCaseId(Number(event.target.value))}>
        {availableCases.map((item) => <option value={item.id} key={item.id}>{item.taxpayer_vkn} · {item.title} · {item.transaction_code}</option>)}
      </select></label>
      {selectedCase && <small>{selectedCase.taxpayer_title} · {selectedCase.period_start} — {selectedCase.period_end} · {selectedCase.transaction_name || selectedCase.claim_type}</small>}
    </section>

    {error && <div className="banner-error" role="alert">{error}<button onClick={() => setError("")}>×</button></div>}
    {busy && !overview && <section className="panel"><div className="empty-state">Raporlar hazırlanıyor…</div></section>}

    {overview && <>
      {caseId !== null && <AccountingReview key={caseId} caseId={caseId} canPrepare={canPrepare} />}
      {overview.scope_message && <div className="issue warning">{overview.scope_message}</div>}
      <section className="panel">
        <div className="panel-head"><div><p className="eyebrow">RESMÎ GİB ÇIKTILARI</p><h2>Zorunlu liste ve tablolar</h2></div><em className={`pill ${overview.portal.ready ? "active" : "partial"}`}>{overview.portal.ready ? "Hazır" : "Eksikler var"}</em></div>
        <div className="report-card-list">
          {overview.portal.outputs.map((output) => {
            const blockers = overview.portal.blockers.filter((item) => messageListCode(item) === output.code);
            const canDownload = output.ready || output.code === "input_vat";
            return <article className="report-card" key={output.code}>
              <div><strong>{output.title}</strong><small>{output.row_count} satır · {output.upload_mode === "manual_entry" ? "Portalda manuel giriş" : `GİB ${output.extension.toUpperCase()} şablonu`}</small></div>
              <em className={`pill ${output.ready ? "active" : "partial"}`}>{output.ready ? "Hazır" : "Eksik"}</em>
              {blockers.length > 0 && <ul>{blockers.slice(0, 3).map((item, index) => <li key={index}>{messageText(item).replace(`${output.title}: `, "")}</li>)}</ul>}
              {output.code === "input_vat" && <>
                <small>191 hesabının alış KDV’sine ait borç kayıtlarından hazırlanır. Faturası bulunamayan kayıtlar fatura alanları boş olarak gösterilir.</small>
                {output.row_count > 0 && <small>Listeyi yeniden hazırladıktan sonra iade hesabını da yeniden çalıştırın.</small>}
                {overview.input_vat_preparation.issues.length > 0 && <ul>{overview.input_vat_preparation.issues.map((issue, index) => <li key={index}>{formatMoneyText(issue)}</li>)}</ul>}
              </>}
              <div className="report-actions">
                {output.code === "input_vat" && canPrepare && <button className="primary-button compact" disabled={busy} onClick={() => void prepareInputVat()}>{output.row_count ? "Listeyi yeniden hazırla" : "Listeyi hazırla"}</button>}
                <button className="text-button" disabled={busy} onClick={() => void showPreview(output.code)}>Önizle</button>
                {output.upload_mode === "excel"
                  ? <a className={`report-download ${canDownload ? "" : "disabled"}`} href={canDownload ? `/api/refund-cases/${caseId}/export/${output.code}?mode=gib` : undefined}>GİB formatında indir</a>
                  : output.code === "reduced_rate_calculation"
                  ? <a className={`report-download ${canDownload ? "" : "disabled"}`} href={canDownload ? `/api/refund-cases/${caseId}/export/${output.code}?mode=review` : undefined}>Excel indir</a>
                  : <span>Portalda manuel girilir</span>}
                {output.code === "loaded_vat" && !output.ready && output.row_count > 0 && <button type="button" className="text-button report-download" disabled={busy} onClick={() => void downloadLoadedVatReview()}>Kontrol için indir</button>}
              </div>
              {output.code === "loaded_vat" && !output.ready && output.row_count > 0 && <small>Kontrol dosyası hesaplanan yüklenimleri içerir. GİB’e yüklemeden önce listedeki eksikleri giderin.</small>}
            </article>;
          })}
        </div>
      </section>

      {preview && <section className="panel report-preview" ref={previewRef}>
        <div className="panel-head"><div><p className="eyebrow">ÖNİZLEME · İLK 50 SATIR</p><h2>{preview.title}</h2></div><button className="text-button" onClick={() => setPreview(null)}>Kapat</button></div>
        {preview.issues.length > 0 && <div className="report-preview-issues">{preview.issues.slice(0, 5).map((item, index) => <span key={index}>{item}</span>)}</div>}
        <div className="report-table-wrap"><table><thead><tr>{preview.columns.map((column) => <th key={column.key}>{column.label}{column.required ? " *" : ""}</th>)}</tr></thead><tbody>
          {preview.rows.map((row, rowIndex) => <tr key={rowIndex}>{preview.columns.map((column) => <td key={column.key}>{/^account_codes?$|^hesap_kodu$/.test(column.key) ? formatAccountCode(String(row[column.key] ?? "")) : row[column.key] !== null && row[column.key] !== undefined && row[column.key] !== "" && column.data_type === "decimal" && !/quantity|rate|ratio|weight|area|volume/.test(column.key) ? moneyNumber(String(row[column.key])) : formatAccountCodeText(String(row[column.key] ?? ""))}</td>)}</tr>)}
        </tbody></table></div>
        {!preview.rows.length && <div className="empty-state">Bu çıktı için henüz veri bulunmuyor.</div>}
      </section>}

      <section className="panel">
        <div className="panel-head"><div><p className="eyebrow">KALAN STOK</p><h2>{overview.remaining_stock.year} yıl sonu stokları</h2></div></div>
        {overview.remaining_stock.available ? <>
          {(overview.remaining_stock.row_count > 0 || overview.remaining_stock.status === "ready") && <p>{overview.remaining_stock.row_count.toLocaleString("tr-TR")} stok satırı · Kalan KDV: {moneyNumber(overview.remaining_stock.remaining_vat)} TL</p>}
          <a className="report-download" href={overview.remaining_stock.url || undefined}>Kalan stok Excel’ini indir</a>
        </> : <div className="issue warning">Kalan stok Excel’i henüz hazır değil. {overview.remaining_stock.issues.length === 1 ? overview.remaining_stock.issues[0] : "Stok hesabındaki eksik bilgileri tamamlayın ve dosyayı yeniden hesaplayın."}</div>}
        {selectedCase?.transaction_code === "439" && <p><button type="button" className="text-button report-download" disabled={busy} onClick={() => void downloadCapacityRecipes()}>Kapasite reçetesini Excel olarak indir</button></p>}
        {overview.remaining_stock.status === "incomplete" && <div className="issue warning">Stok hesabı eksik. Excel yalnız hesaplanabilen bakiyeleri içerir; hesaplanamayan kayıtlar Kontrol notları sayfasındadır.</div>}
          {overview.remaining_stock.issues.length > 1 && <details>
            <summary>Stok hesabındaki eksikleri göster ({overview.remaining_stock.issues.length})</summary>
            <ul>{overview.remaining_stock.issues.map((issue, index) => <li key={index}>{issue}</li>)}</ul>
          </details>}
      </section>

      <section className="panel report-package-panel">
        <div className="panel-head"><div><p className="eyebrow">BAŞVURU PAKETİ</p><h2>Manuel GİB yükleme paketi</h2></div>{latestPackage && <em className={`pill ${latestPackage.status === "ready" ? "active" : "partial"}`}>v{latestPackage.version} · {reportStatus(latestPackage.status)}</em>}</div>
        <p>{overview.submission_capability.reason}</p>
        <div className="report-package-actions">
          {canPrepare && <button className="primary-button compact" disabled={busy} onClick={() => void createPackage()}>Paketi hazırla</button>}
          {latestPackage?.status === "ready" && <button className="secondary-button compact" disabled={busy} onClick={() => void downloadPackage(latestPackage.id)}>ZIP paketi indir</button>}
          {canApprove && latestPackage?.status === "ready" && !latestPackage.approval && <button className="secondary-button compact" disabled={busy} onClick={() => void approvePackage(latestPackage.id)}>Paketi onayla</button>}
        </div>
        <ol className="report-steps"><li>Hazır GİB Excel listelerini indirin.</li><li>Manuel giriş olarak işaretlenen tabloları portal ekranında doldurun.</li><li>Dosyaları İnternet Vergi Dairesi’ne yükleyip portal kontrollerini çalıştırın.</li><li>Portalda “Onayla” adımını kullanıcı olarak tamamlayın.</li></ol>
        <a className="text-button report-guide" href={overview.submission_capability.official_guide_url} target="_blank" rel="noreferrer">Resmî GİB kılavuzunu aç</a>
      </section>
    </>}
  </div>;
}


function CustomsExports({taxpayer}: {taxpayer: string}) {
  const year = new Date().getFullYear();
  const [start, setStart] = useState(`${year}-01-01`);
  const [end, setEnd] = useState(`${year}-12-31`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function download(kind: "gcb" | "sales") {
    setBusy(true); setError("");
    try {
      const params = new URLSearchParams({taxpayer, start, end});
      const file = await apiFile(`/api/customs-documents/export/${kind}?${params}`);
      saveBlob(file.blob, file.filename, `${kind}.xls`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setBusy(false); }
  }
  return <section className="panel customs-reports"><div className="panel-head"><h2>GGB / GÇB raporları</h2></div>
    <div className="customs-filters"><label>Kapanma dönemi başlangıcı<input type="date" value={start} onChange={e => setStart(e.target.value)} /></label>
      <label>Kapanma dönemi bitişi<input type="date" value={end} onChange={e => setEnd(e.target.value)} /></label></div>
    <div className="customs-upload-actions"><button className="secondary-button" disabled={busy || !taxpayer || !start || !end || start > end} onClick={() => void download("gcb")}>GÇB / ETGB Listesi indir (.xls)</button>
      <button className="primary-button" disabled={busy || !taxpayer || !start || !end || start > end} onClick={() => void download("sales")}>301 ve 338 Satış Faturaları Listesi indir (.xls)</button></div>
    {error && <p role="alert">{error}</p>}
  </section>;
}
